import Loader from "@components/Loader";
import AdvancementAccordion from "@components/dashboard/advancements/AdvancementAccordion";
import AiActionComposer from "@components/dashboard/ai/actions/AiActionComposer";
import AiPanel from "@components/dashboard/disbursement/ai/AiPanel";
import useAiQuota, { quotaNote } from "@components/dashboard/disbursement/ai/useAiQuota";
import DisbursementWorkspace from "@components/dashboard/disbursement/DisbursementWorkspace";
import ScheduleCurveChart from "@components/dashboard/disbursement/ScheduleCurveChart";
import SliceAdjustForm from "@components/dashboard/disbursement/SliceAdjustForm";
import { labelOfCompetence } from "@components/dashboard/disbursement/months";
import { contributionOf, monthlyAvailability } from "@components/dashboard/disbursement/slice";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { formatNumberToCurrency } from "@utils/formatNumber";
import { useEffect, useRef, useState } from "react";
import { isPresentationProject } from "@utils/presentationMode";
import { isDisbursementPlanningReadOnly } from "@components/dashboard/financialProjection/cashFlowPlanning";


const renderRow = (label, value) => {
  return <div className="data">
    <span className="accordion-label">{label}</span>
    <span className="value">{value}</span>
  </div>
}

const CreatedItem = ({ resource, handleDelete, readOnly }) => {
  const [open, setOpen] = useState(true)

  return <div className={`accordion-item offcanvas-accordion-item curve margin-bottom-8`}>
    <div>
      <div className="flex align-center margin-bottom-8">
        <button
          className={`accordion-toggle ${open ? "active" : ""}`}
          onClick={() => setOpen(!open)}
        />
        <span className="accordion-title" title={resource.name} >{resource.name}</span>
      </div>
    </div>
    {open && resource.data.map(i => {
      return <div key={i.entryId || `${i.from}-${i.to}-${i.value}-${i.updated_at || ''}`} className="grid gap-16 columns-5-laptop-l margin-top-12">
        {renderRow(`Movido de`, i.from)}
        {renderRow(`Movido para`, i.to)}
        {renderRow('Criado em', new Date(i.updated_at).toLocaleDateString('pt-BR'))}
        {renderRow('Valor', formatNumberToCurrency(i.value))}
        {!readOnly && <button style={{ justifySelf: 'end' }} className="button button-error small" onClick={() => handleDelete(i, resource)}>Deletar</button>}
      </div>
    })}
  </div>
}


/** `[{ year, month, value }]` do item (reais) para os pontos do recorte. */
const monthlyOf = (item) => (Array.isArray(item?.monthlyDisbursement) ? item.monthlyDisbursement : []);

/**
 * Cabeçalho de um item no recorte por meses (planning-workspace): o que ele soma nos meses
 * selecionados e o ajuste a partir deles. O formulário é o mesmo do módulo Ampli; aqui ele
 * grava um `advancement` por mês de origem usado.
 */
const SliceItemSummary = ({ item, selectedMonths, readOnly, onSubmit }) => {
  const [adjusting, setAdjusting] = useState(false);
  const points = monthlyOf(item);
  const availability = monthlyAvailability(points, selectedMonths);
  const total = contributionOf(points, selectedMonths);
  return (
    <div className="advancement-slice-item" data-testid="slice-item">
      <p className="accordion-label">
        {`Meses selecionados: ${formatNumberToCurrency(total)}`}
      </p>
      {!readOnly ? (
        <button
          type="button"
          className="button button-secondary small"
          aria-expanded={adjusting}
          onClick={() => setAdjusting((value) => !value)}
        >
          Ajustar desembolso
        </button>
      ) : null}
      {adjusting && !readOnly ? (
        <SliceAdjustForm
          label={item.wbs_description}
          availability={availability}
          onCancel={() => setAdjusting(false)}
          onDone={() => setAdjusting(false)}
          submitLines={(lines, toCompetence, description) =>
            onSubmit(item, lines, toCompetence, description)
          }
        />
      ) : null}
    </div>
  );
};

const Advancements = () => {
  const { getAppropriation, getAdvancements, updateAdvancement, getAllBuildingItems } = useProjectActions();
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(false)
  const [allAdvancements, setAllAdvancements] = useState([])
  const [search, setSearch] = useState('')
  const [createdVisible, setCreatedVisible] = useState(true)
  const [openItems, setOpenItems] = useState({});
  const [levelToGet, setLevelToGet] = useState('')
  const [levelItems, setLevelItems] = useState([])
  const [itemToFilter, setItemToFilter] = useState('')

  const toggleItem = (wbs_code_id) => {
    setOpenItems((prev) => ({
      ...prev,
      [wbs_code_id]: !prev[wbs_code_id],
    }));
    const element = document.getElementById(wbs_code_id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const { activeProject } = useProjectContext()
  const readOnly = isPresentationProject(activeProject)
  const usesPhysicalFinancial = isDisbursementPlanningReadOnly(
    activeProject?.cashFlowPlanning,
  )
  const planningReadOnly = readOnly || usesPhysicalFinancial
  // A IA é um recurso dedicado em painel (planning-workspace): só existe montada enquanto
  // aberta, fecha na troca de obra e nem o botão aparece quando a escrita não é permitida.
  const workId = activeProject?._id
  const [aiOpen, setAiOpen] = useState(false)
  useEffect(() => {
    setAiOpen(false)
  }, [workId])
  const aiAccess = useAiQuota(workId, Boolean(workId) && !planningReadOnly)
  const activeWorkIdRef = useRef(activeProject?._id)
  const requestGenerationRef = useRef(0)
  activeWorkIdRef.current = activeProject?._id

  const getItems = async (project) => {
    const generation = ++requestGenerationRef.current
    const workId = project?._id
    const buildingId = project?.building_id
    const isCurrentRequest = () => (
      activeWorkIdRef.current === workId
      && requestGenerationRef.current === generation
    )
    if (!workId || !buildingId) {
      if (isCurrentRequest()) {
        setResources([])
        setAllAdvancements([])
        setLoading(false)
      }
      return
    }
    setLoading(true)
    try {
      const response = await getAppropriation(project.appropriation_level)
      if (!isCurrentRequest()) return
      setResources([...(response?.uniqueDescs || [])].sort((a, b) => a.wbs_description.localeCompare(b.wbs_description)))
      const all = await getAdvancements(buildingId)
      if (!isCurrentRequest()) return
      setAllAdvancements(Array.isArray(all?.response) ? all.response : [])
    } finally {
      if (isCurrentRequest()) setLoading(false)
    }
  }

  useEffect(() => {
    getItems(activeProject)
  }, [activeProject])

  const refreshAdvancements = async () => {
    const generation = ++requestGenerationRef.current
    const workId = activeProject?._id
    const buildingId = activeProject?.building_id
    if (!workId || !buildingId) return
    const isCurrentRefresh = () => (
      activeWorkIdRef.current === workId
      && requestGenerationRef.current === generation
    )
    try {
      const refreshed = await getAdvancements(buildingId)
      if (!isCurrentRefresh()) return
      setAllAdvancements(Array.isArray(refreshed?.response) ? refreshed.response : [])
    } catch {
      // Estas outlets não exibem erro de carga; preserva os dados atuais.
    } finally {
      if (isCurrentRefresh()) setLoading(false)
    }
  }

  const getLevelItems = async () => {
    const res = await getAllBuildingItems([levelToGet])
    setLevelItems(res.items)
  }

  useEffect(() => {
    if (levelToGet !== '') {
      getLevelItems()

    }
  }, [levelToGet])

  const handleDelete = async (item, resource) => {
    const response = await updateAdvancement({
      operation: 'remove',
      wbs: resource.wbs,
      ...(item.entryId ? { entryId: item.entryId } : {}),
      from: item.from,
      to: item.to,
    })
    if (response?.code === 200) {
      const refreshed = await getAdvancements(activeProject.building_id)
      setAllAdvancements(Array.isArray(refreshed?.response) ? refreshed.response : [])
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);

    const filteredResources = resources.filter((i) =>
      i.description.toLowerCase().includes(value.toLowerCase())
    );
    const newOpenItems = {};
    filteredResources.forEach((item) => {
      newOpenItems[item.wbs_code_id] = openItems[item.wbs_code_id] || false;
    });
    setOpenItems(newOpenItems);
  };


  // Ajuste a partir do recorte: um `advancement` por mês de origem usado, o valor repartido do
  // mês mais antigo ao mais novo. Para no primeiro que falhar.
  const adjustFromSlice = async (item, lines, toCompetence, description) => {
    if (planningReadOnly) return { ok: false, message: 'Ajustes desativados para esta obra.' }
    let last = null
    for (const line of lines) {
      last = await updateAdvancement({
        operation: 'create',
        name: item.description,
        value: line.amountCents / 100,
        description,
        to: labelOfCompetence(toCompetence),
        from: labelOfCompetence(line.competence),
        wbs: item.wbs_code_id,
      })
      if (last?.code !== 200) return { ok: false }
    }
    await refreshAdvancements()
    return { ok: true }
  }

  const visibleResources = (selectedMonths) => resources
    .filter(i => i.wbs_code_id.includes(itemToFilter || ''))
    .filter(i => i.wbs_description.toLowerCase().includes(search.toLowerCase()))
    .filter(i => selectedMonths.size === 0 || contributionOf(monthlyOf(i), selectedMonths) > 0)

  const createdBlock = (
    <div className="resources-container">
      <div className="flex align-center margin-bottom-8">
        <button
          className={`accordion-toggle ${createdVisible ? "active" : ""}`}
          onClick={() => setCreatedVisible(!createdVisible)}
          aria-expanded={createdVisible}
          aria-label="Ajustes já criados"
        />
        <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'grey' }}>Ajustes já criados</span>
      </div>
      {createdVisible && (
        allAdvancements?.some(i => i.data?.length > 0)
          ? allAdvancements.filter(i => i.data?.length > 0).map((resource) => {
            return <CreatedItem key={resource._id || `${resource.building_id}-${resource.wbs}`} resource={resource} handleDelete={handleDelete} readOnly={planningReadOnly} />
          })
          : <p className="empty-state">Nenhum ajuste de mês foi criado nesta obra ainda.</p>
      )}
    </div>
  )

  return (
    <DisbursementWorkspace
      workId={workId}
      status={<>
        {loading && <div className="margin-top-24"><Loader /></div>}
        <div className="resources-container">
          {usesPhysicalFinancial ? (
            <div className="alert alert-info margin-bottom-24" role="status">
              <h3>Ajustes fora da previsão atual</h3>
              <p>A organização usa o previsto físico-financeiro. Os ajustes de desembolso permanecem visíveis para consulta, mas não alteram a previsão de caixa.</p>
            </div>
          ) : null}
          {!planningReadOnly && workId ? (
            <div className="ampli-disbursement-header__actions">
              <button
                type="button"
                className="button button-secondary small"
                data-testid="advancement-ai-open"
                onClick={() => setAiOpen(true)}
              >
                Criar com IA
              </button>
              {quotaNote(aiAccess.quota) ? (
                <span className="ampli-disbursement-header__ai-quota" data-testid="advancement-ai-quota">
                  {quotaNote(aiAccess.quota)}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </>}
      chart={({ selectedMonths, toggleMonth, clearMonths }) => (
        <ScheduleCurveChart
          project={activeProject}
          selectedMonths={selectedMonths}
          onToggleMonth={toggleMonth}
          onClearMonths={clearMonths}
        />
      )}
      created={createdBlock}
      detail={({ selectedMonths }) => {
        const sliceActive = selectedMonths.size > 0
        const listed = visibleResources(selectedMonths)
        return (
          <div className="resources-container">
            {!readOnly && <>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'grey' }}>Todos itens</p>
              <div className="flex justify-content-center align-center gap-8">
                <span>Filtrar por:</span>
                <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => {
                  setLevelToGet(e.target.value)
                  setItemToFilter('')
                }} value={levelToGet}>
                  <option value='' disabled>Selecione</option>
                  <option value='units'>Unidade Construtiva</option>
                  <option value='cells'>Célula</option>
                  <option value='steps'>Etapa</option>
                  <option value='substeps'>Subetapa</option>
                  <option value='services'>Serviço</option>
                </select>
                {levelItems.length > 0 && <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => setItemToFilter(e.target.value)} value={itemToFilter}>
                  <option value='' disabled>Selecione</option>
                  {levelItems.map(i => {
                    return <option key={i.wbs_code_id} value={i.wbs_code_id}>{i.building_unit_description || i.wbs_description}</option>
                  })}
                </select>}
                {itemToFilter !== '' && <button style={{ alignSelf: 'center' }} className="button button-secondary" onClick={() => setItemToFilter('')}>Limpar</button>}
              </div>
              <input type="text" value={search} onChange={handleSearch} placeholder="Buscar por item" />
              {sliceActive ? (
                <p className="card-label">
                  {`Itens com desembolso em ${selectedMonths.size === 1 ? 'o mês selecionado' : 'algum dos meses selecionados'}; o valor de cada um é a soma desses meses.`}
                </p>
              ) : null}
              {sliceActive && resources?.length > 0 && listed.length === 0 ? (
                <p className="empty-state" role="status">Nenhum item compõe o desembolso do mês selecionado.</p>
              ) : null}
              <div className="accordion">
                {resources?.length > 0 && (
                  <>
                    <div className="accordion">
                      {listed.map(i => {
                        return <div key={i.wbs_code_id}>
                          {sliceActive ? (
                            <SliceItemSummary
                              item={i}
                              selectedMonths={selectedMonths}
                              readOnly={planningReadOnly}
                              onSubmit={adjustFromSlice}
                            />
                          ) : null}
                          <AdvancementAccordion
                            item={i}
                            wbs={i.wbs_code_id}
                            isOpen={!!openItems[i.wbs_code_id]}
                            toggleItem={toggleItem}
                          />
                        </div>
                      })}
                    </div>
                  </>
                )}
              </div>
            </>}
          </div>
        )
      }}
      panel={aiOpen && !planningReadOnly && workId ? (
        <AiPanel title="Criar com IA" onClose={() => setAiOpen(false)}>
          <AiActionComposer
            workId={workId}
            module="advancement"
            onExecuted={async (result) => {
              await refreshAdvancements()
              setAiOpen(false)
              return result
            }}
          />
        </AiPanel>
      ) : null}
    />
  );
};

export default Advancements;
